import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CloseOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Alert, Button, Checkbox, Input, Modal, Select, Spin, Table, Tooltip, message } from "antd";
import { deleteSprNumberCat, fetchSprNumberCatList } from "./spr-number-cat-api";
import SprNumberCat from "./spr-number-cat";
import searchSrc from "../number/assets/search.svg";
import "./spr-number-cats.css";

const SEARCH_DEBOUNCE_MS = 300;

const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  const n = Number(law[index]);
  return Number.isFinite(n) ? n : 1;
};

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return undefined;
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toFlag = (value) => Number(value) === 1;

const defaultListFilters = () => ({
  q: "",
  onlyActive: true
});

const filtersFromSearchParams = (params) => {
  const src =
    params instanceof URLSearchParams ? params : new URLSearchParams(params || "");
  const base = defaultListFilters();
  const q = src.has("q") ? src.get("q") ?? "" : "";
  let onlyActive = base.onlyActive;
  if (src.has("active")) {
    const a = src.get("active");
    onlyActive = a === "1" || a === "true";
  }
  return { q, onlyActive };
};

const filtersToSearchParams = (filters) => {
  const params = new URLSearchParams();
  const q = typeof filters?.q === "string" ? filters.q.trim() : "";
  if (q) params.set("q", q);
  if (filters?.onlyActive) params.set("active", "1");
  else params.set("active", "0");
  return params;
};

const listSearchString = (filters) => {
  const s = filtersToSearchParams(filters).toString();
  return s ? `?${s}` : "";
};

const normalizeRow = (row) => ({
  idNumberCat: toNumber(pick(row, "idNumberCat", "IdNumberCat")),
  name: pick(row, "name", "Name") ?? "",
  cntNumbers: toNumber(pick(row, "cntNumbers", "CntNumbers")) ?? 0,
  cntMest: toNumber(pick(row, "cntMest", "CntMest")) ?? 0,
  cntDopMest: toNumber(pick(row, "cntDopMest", "CntDopMest")) ?? 0,
  isAdd: toFlag(pick(row, "isAdd", "IsAdd")),
  dayChange: toNumber(pick(row, "dayChange", "DayChange")),
  vidPitaniaName: pick(row, "vidPitaniaName", "VidPitaniaName") ?? "",
  isDel: toFlag(pick(row, "isDel", "IsDel"))
});

const MiniSelect = ({ autoFocus, defaultOpen, options, placeholder, value, onChange, width }) => (
  <Select
    autoFocus={autoFocus}
    defaultOpen={defaultOpen}
    showSearch
    optionFilterProp="label"
    placeholder={placeholder}
    value={value}
    options={options}
    popupMatchSelectWidth={false}
    variant="borderless"
    suffixIcon={null}
    style={{ width }}
    onChange={onChange}
  />
);

const FILTER_KIND = { ACTIVE: "active" };
const ACTIVE_OPTIONS = [{ value: 1, label: "Активные" }];

const CatsSearchFilter = ({ name, onNameChange, onlyActive, onOnlyActiveChange }) => {
  const [adding, setAdding] = useState(false);
  const [editKind, setEditKind] = useState(null);
  const [focused, setFocused] = useState(false);

  const chips = [];
  if (onlyActive) {
    chips.push({
      key: FILTER_KIND.ACTIVE,
      kindLabel: "Активность",
      valueLabel: "Активные"
    });
  }

  const canAdd = !onlyActive;

  const startAdd = () => {
    setEditKind(null);
    onOnlyActiveChange(true);
    setAdding(false);
  };

  const cancelAdd = () => {
    setAdding(false);
    setEditKind(null);
  };

  const clearKind = () => {
    onOnlyActiveChange(false);
    setEditKind(null);
  };

  return (
    <div
      className={`spr-number-cats__search${focused ? " spr-number-cats__search--focused" : ""}`}
      role="group"
      aria-label="Поиск и фильтр"
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false);
      }}
    >
      <span className="spr-number-cats__search-icon">
        <img src={searchSrc} alt="" />
      </span>
      <Input
        variant="borderless"
        allowClear
        className="spr-number-cats__search-input"
        value={name}
        placeholder="Поиск по наименованию..."
        onChange={(e) => onNameChange(e.target.value)}
      />
      {chips.map((chip) =>
        editKind === chip.key ? (
          <span key={chip.key} className="spr-number-cats__search-draft">
            <span className="spr-number-cats__search-chip-kind">{chip.kindLabel}</span>
            <MiniSelect
              autoFocus
              defaultOpen
              options={ACTIVE_OPTIONS}
              placeholder="Значение"
              value={1}
              width={160}
              onChange={() => setEditKind(null)}
            />
            <button
              type="button"
              className="spr-number-cats__search-chip-clear"
              title="Закрыть"
              aria-label="Закрыть"
              onClick={() => setEditKind(null)}
            >
              <CloseOutlined />
            </button>
          </span>
        ) : (
          <span key={chip.key} className="spr-number-cats__search-chip">
            <button
              type="button"
              className="spr-number-cats__search-chip-btn"
              title="Изменить значение"
              onClick={() => {
                setAdding(false);
                setEditKind(chip.key);
              }}
            >
              <span className="spr-number-cats__search-chip-kind">{chip.kindLabel}</span>
              <span className="spr-number-cats__search-chip-value">{chip.valueLabel}</span>
            </button>
            <button
              type="button"
              className="spr-number-cats__search-chip-clear"
              title="Сбросить"
              aria-label={`Сбросить: ${chip.kindLabel}`}
              onClick={clearKind}
            >
              <CloseOutlined />
            </button>
          </span>
        )
      )}
      {adding ? (
        <span className="spr-number-cats__search-draft">
          <MiniSelect
            autoFocus
            defaultOpen
            options={[{ value: FILTER_KIND.ACTIVE, label: "Активность" }]}
            placeholder="Фильтр"
            width={130}
            onChange={() => {
              onOnlyActiveChange(true);
              cancelAdd();
            }}
          />
          <button
            type="button"
            className="spr-number-cats__search-chip-clear"
            title="Отменить"
            aria-label="Отменить добавление фильтра"
            onClick={cancelAdd}
          >
            <CloseOutlined />
          </button>
        </span>
      ) : null}
      {canAdd && !adding && !editKind ? (
        <button
          type="button"
          className="spr-number-cats__search-add"
          title="Добавить фильтр"
          aria-label="Добавить фильтр"
          onClick={startAdd}
        >
          <PlusOutlined />
        </button>
      ) : null}
    </div>
  );
};

/**
 * Список категорий номеров (SprNumberCats). Delphi: вкладка «Категории номеров».
 */
const SprNumberCats = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = routeId === "new";
  const editId = routeId && /^\d+$/.test(routeId) ? Number(routeId) : null;
  const drawerOpen = isNew || editId != null;
  const urlFilters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);
  const law = useSelector((state) => state.user.session?.law);
  const canEdit = getLawFlag(law, 4) === 1;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [nameFilter, setNameFilter] = useState(() => urlFilters.q);
  const [debouncedQ, setDebouncedQ] = useState(() => (urlFilters.q || "").trim());
  const [onlyActive, setOnlyActive] = useState(() => urlFilters.onlyActive);
  const [selectedId, setSelectedId] = useState(null);
  const [checkedIds, setCheckedIds] = useState([]);
  const requestIdRef = useRef(0);
  const skipUrlSyncRef = useRef(false);

  const listFilters = useMemo(
    () => ({ q: debouncedQ, onlyActive }),
    [debouncedQ, onlyActive]
  );

  const listFiltersLive = useMemo(
    () => ({ q: nameFilter, onlyActive }),
    [nameFilter, onlyActive]
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ((nameFilter || "").trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [nameFilter]);

  useEffect(() => {
    const next = filtersToSearchParams(listFilters);
    if (searchParams.toString() === next.toString()) return;
    skipUrlSyncRef.current = true;
    setSearchParams(next, { replace: true });
  }, [listFilters, searchParams, setSearchParams]);

  useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }
    const fromUrl = filtersFromSearchParams(searchParams);
    setOnlyActive((prev) => (prev === fromUrl.onlyActive ? prev : fromUrl.onlyActive));
    setNameFilter((prev) => (prev === fromUrl.q ? prev : fromUrl.q));
    setDebouncedQ((prev) => {
      const q = (fromUrl.q || "").trim();
      return prev === q ? prev : q;
    });
  }, [searchParams]);

  const goList = useCallback(
    (opts = {}) => {
      const { replace = false } = opts;
      navigate(`/spr/number-cat${listSearchString(listFiltersLive)}`, { replace });
    },
    [navigate, listFiltersLive]
  );

  const loadItems = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    fetchSprNumberCatList()
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        setRows((Array.isArray(data) ? data : []).map(normalizeRow).filter((r) => r.idNumberCat != null));
      })
      .catch((e) => {
        if (requestId !== requestIdRef.current) return;
        setError(e.message || "Не удалось загрузить категории");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (canEdit) loadItems();
  }, [canEdit, loadItems]);

  useEffect(() => {
    if (editId != null) setSelectedId(editId);
  }, [editId]);

  useEffect(() => {
    if (editId == null || loading || !rows.length) return;
    if (!rows.some((r) => r.idNumberCat === editId)) {
      goList({ replace: true });
    }
  }, [editId, rows, loading, goList]);

  const filteredRows = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    return rows.filter((r) => {
      if (onlyActive && r.isDel) return false;
      if (!q) return true;
      return String(r.name || "")
        .toLowerCase()
        .includes(q);
    });
  }, [rows, nameFilter, onlyActive]);

  const checkedRows = useMemo(
    () => filteredRows.filter((r) => checkedIds.includes(r.idNumberCat)),
    [filteredRows, checkedIds]
  );
  const hasChecked = checkedRows.length > 0;

  const openEdit = useCallback(
    (record) => {
      if (record?.idNumberCat == null) return;
      setSelectedId(record.idNumberCat);
      navigate(`/spr/number-cat/${record.idNumberCat}${listSearchString(listFiltersLive)}`);
    },
    [navigate, listFiltersLive]
  );

  const openCreate = useCallback(() => {
    if (!canEdit) return;
    navigate(`/spr/number-cat/new${listSearchString(listFiltersLive)}`);
  }, [canEdit, navigate, listFiltersLive]);

  const closeDrawer = useCallback(() => {
    goList();
  }, [goList]);

  const onSaved = useCallback(
    (saved) => {
      loadItems();
      const newId = toNumber(pick(saved || {}, "idNumberCat", "IdNumberCat"));
      if (newId != null) {
        setSelectedId(newId);
        navigate(`/spr/number-cat/${newId}${listSearchString(listFiltersLive)}`, { replace: true });
      }
    },
    [loadItems, navigate, listFiltersLive]
  );

  const onDeleteChecked = useCallback(() => {
    if (!canEdit || !checkedRows.length) return;
    const ids = checkedRows.map((r) => r.idNumberCat);
    const many = ids.length > 1;
    Modal.confirm({
      title: many ? `Удалить категории (${ids.length})?` : "Удалить категорию номера?",
      content: many
        ? checkedRows.map((r) => r.name || `№${r.idNumberCat}`).join(", ")
        : checkedRows[0].name || `№${ids[0]}`,
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          for (const id of ids) {
            await deleteSprNumberCat(id);
          }
          message.success("Удалено");
          if (ids.includes(editId)) goList();
          if (ids.includes(selectedId)) setSelectedId(null);
          setCheckedIds([]);
          loadItems();
        } catch (e) {
          message.error(e.message || "Не удалось удалить");
          loadItems();
          throw e;
        }
      }
    });
  }, [canEdit, checkedRows, editId, selectedId, loadItems, goList]);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: checkedIds,
      columnWidth: 36,
      onChange: (keys) => setCheckedIds(keys)
    }),
    [checkedIds]
  );

  const columns = useMemo(
    () => [
      {
        title: "",
        label: "Статус",
        key: "status",
        width: 44,
        align: "center",
        render: (_, record) => {
          const hidden = Boolean(record.isDel);
          const label = hidden ? "Скрыта" : "Активна";
          return (
            <Tooltip title={label}>
              <span
                className="spr-number-cats__status-dot"
                data-tone={hidden ? "off" : "on"}
                aria-label={label}
              />
            </Tooltip>
          );
        }
      },
      {
        title: "#",
        dataIndex: "idNumberCat",
        key: "idNumberCat",
        width: 70
      },
      {
        title: "Наименование",
        dataIndex: "name",
        key: "name",
        className: "spr-number-cats__col-name",
        ellipsis: true
      },
      {
        title: "Кол.",
        dataIndex: "cntNumbers",
        key: "cntNumbers",
        width: 70,
        align: "right"
      },
      {
        title: "Основные",
        dataIndex: "cntMest",
        key: "cntMest",
        width: 90,
        align: "right"
      },
      {
        title: "Доп.",
        dataIndex: "cntDopMest",
        key: "cntDopMest",
        width: 70,
        align: "right"
      },
      {
        title: "Подселение",
        dataIndex: "isAdd",
        key: "isAdd",
        width: 110,
        align: "center",
        className: "spr-number-cats__col-flag",
        render: (v) => (
          <Checkbox className="spr-number-cats__flag" checked={Boolean(v)} onChange={() => {}} />
        )
      },
      {
        title: "Питание",
        dataIndex: "vidPitaniaName",
        key: "vidPitaniaName",
        width: 160,
        ellipsis: true
      }
    ],
    []
  );

  if (!canEdit) {
    return (
      <div className="spr-number-cats">
        <Alert type="warning" showIcon message="Нет права «Настройка номеров»" />
      </div>
    );
  }

  const hasFilter = Boolean(nameFilter.trim());

  return (
    <div className="spr-number-cats">
      <div className="spr-number-cats__toolbar">
        <div className="spr-number-cats__extra-actions">
          <Tooltip title="Создать категорию">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              aria-label="Создать категорию"
              onClick={openCreate}
            />
          </Tooltip>
          <Tooltip title={hasChecked ? "Удалить" : "Отметьте категории"}>
            <span>
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={!hasChecked}
                onClick={onDeleteChecked}
              />
            </span>
          </Tooltip>
        </div>
        <CatsSearchFilter
          name={nameFilter}
          onNameChange={setNameFilter}
          onlyActive={onlyActive}
          onOnlyActiveChange={setOnlyActive}
        />
      </div>
      {error ? (
        <Alert className="spr-number-cats__error" type="error" showIcon message={error} />
      ) : null}
      <div className="spr-number-cats__body">
        {loading && !rows.length ? (
          <div className="spr-number-cats__center">
            <Spin tip="Загрузка категорий..." />
          </div>
        ) : (
          <Table
            className="spr-number-cats__table"
            size="small"
            rowKey="idNumberCat"
            columns={columns}
            dataSource={filteredRows}
            loading={loading}
            pagination={false}
            rowSelection={rowSelection}
            onRow={(record) => ({
              onClick: (e) => {
                if (e.target.closest(".ant-checkbox-wrapper, .ant-table-selection-column")) {
                  return;
                }
                openEdit(record);
              }
            })}
            rowClassName={(record) =>
              [
                "spr-number-cats__row",
                record.isDel ? "spr-number-cats__row--hidden" : "",
                selectedId != null && record.idNumberCat === selectedId
                  ? "spr-number-cats__row--selected"
                  : "",
                editId != null && record.idNumberCat === editId
                  ? "spr-number-cats__row--editing"
                  : ""
              ]
                .filter(Boolean)
                .join(" ")
            }
            scroll={{ x: 900 }}
            locale={{
              emptyText: hasFilter ? "Нет категорий по фильтру" : "Нет категорий"
            }}
          />
        )}
      </div>
      <SprNumberCat
        open={drawerOpen}
        idNumberCat={editId}
        isNew={isNew}
        onClose={closeDrawer}
        onSaved={onSaved}
      />
    </div>
  );
};

export default SprNumberCats;
